import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Drawer,
  Empty,
  Input,
  List,
  message,
  Modal,
  Radio,
  Skeleton,
  Space,
  Steps,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { richTextMarkup } from "./richTextUtils";
import "./JobApplications.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const APPLICATION_STATUS_FILTERS = [
  "ALL",
  "SUBMITTED",
  "UNDER_REVIEW",
  "SHORTLISTED",
  "INTERVIEWING",
  "OFFERED",
  "HIRED",
  "REJECTED",
  "WITHDRAWN",
  "HIRED_CLOSED",
  "REJECTED_CLOSED",
  "CANCELLED_CLOSED",
];

const queryParam = (search, key) => new URLSearchParams(search).get(key);

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const displayText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const compactTextList = (values = []) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map((value) => {
      if (typeof value === "string") return value.trim();
      if (typeof value === "number") return String(value);
      if (value && typeof value === "object") {
        return firstText(value.name, value.title, value.label, value.code, value.description, value.city);
      }
      return "";
    })
    .filter(Boolean);

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return titleCase(value);
  return displayText(value.name, value.label, value.title, value.code);
};

const profileTitle = (profile) =>
  firstText(profile?.profileName, profile?.title, profile?.name) ||
  `${titleCase(profile?.sector || profile?.sectorCode || "General")} Professional Profile`;

const profileSubtitle = (profile) =>
  firstText(profile?.description) ||
  compactTextList([...(profile?.sectors || []), profile?.sector, profile?.sectorCode]).join(", ") ||
  "Professional profile";

const ProfileSelectOption = ({ profile }) => {
  const summary = firstText(profile?.profileSummary);

  return (
    <>
      <strong>{profileTitle(profile)}</strong>
      {summary ? (
        <div
          className="job-application-select-card__summary olreadia-rich-text"
          dangerouslySetInnerHTML={richTextMarkup(summary)}
        />
      ) : (
        <span>{profileSubtitle(profile)}</span>
      )}
    </>
  );
};

const profileServices = (profile) => compactTextList(profile?.interestedServices || []).slice(0, 4);
const profileLocations = (profile) => compactTextList(profile?.interestedLocations || []).slice(0, 4);
const profileShifts = (profile) => compactTextList(profile?.interestedShifts || []).map(titleCase).slice(0, 4);

const backgroundCheckLabel = (check) =>
  [titleCase(check?.checkType || "Background Check"), titleCase(check?.status)].filter(Boolean).join(" • ");

const eligibleBackgroundCheck = (check) => {
  const status = String(check?.status || "").toUpperCase();
  return status === "PASSED" || status === "FAILED" || status === "COMPLETED";
};

const SNAPSHOT_SECTIONS = {
  userDetails: "User Details",
  profile: "Professional Profile",
  sectors: "Sectors",
  regions: "Previous Regions",
  references: "References",
  workExperience: "Work Experience",
  education: "Education",
  documents: "Documents",
  photos: "Photos",
  certifications: "Certifications",
  licenses: "Licenses",
  interestedServices: "Interested Services",
  interestedLocations: "Interested Locations",
  interestedShifts: "Interested Shifts",
};

const STRUCTURED_SNAPSHOT_SECTIONS = [
  "sectors",
  "references",
  "workExperience",
  "education",
  "documents",
  "photos",
  "certifications",
  "licenses",
];

const ATTACHMENT_SNAPSHOT_SECTIONS = ["documents", "photos", "certifications", "licenses"];

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || "").trim());

const isIdKey = (key) => /(^id$|id$|ids$)/i.test(String(key || "").trim());

const sanitizeSnapshotValue = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return isUuid(value) ? "" : value;
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    return value
      .map(sanitizeSnapshotValue)
      .filter((item) => item != null && item !== "" && !(Array.isArray(item) && item.length === 0));
  }
  if (typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nextValue]) => {
      if (isIdKey(key)) return acc;
      const sanitized = sanitizeSnapshotValue(nextValue);
      if (sanitized == null || sanitized === "") return acc;
      if (Array.isArray(sanitized) && sanitized.length === 0) return acc;
      if (typeof sanitized === "object" && !Array.isArray(sanitized) && Object.keys(sanitized).length === 0) return acc;
      acc[key] = sanitized;
      return acc;
    }, {});
  }
  return value;
};

const snapshotString = (value) => {
  const sanitized = sanitizeSnapshotValue(value);
  if (sanitized == null) return "";
  if (typeof sanitized === "string") return sanitized;
  if (typeof sanitized === "number" || typeof sanitized === "boolean") return String(sanitized);
  if (Array.isArray(sanitized) && sanitized.length === 0) return "";
  if (typeof sanitized === "object" && Object.keys(sanitized).length === 0) return "";
  return JSON.stringify(sanitized);
};

const tryParseSnapshotValue = (value) => {
  const text = String(value || "").trim();
  if (!text || (!text.startsWith("{") && !text.startsWith("["))) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const isOpenableFileUri = (value) => /^(https?:|file:|content:|ph:)/i.test(String(value || "").trim());

const fileUriFromValue = (value) => {
  if (typeof value === "string") return isOpenableFileUri(value) ? value.trim() : "";
  if (!value || typeof value !== "object") return "";
  return firstText(
    value.signedUrl,
    value.signedURL,
    value.downloadUrl,
    value.downloadURL,
    value.fileUrl,
    value.fileURL,
    value.url,
    value.uri,
    value.localUri,
    value.fileLocalUri,
    isOpenableFileUri(value.fileId) ? value.fileId : "",
    isOpenableFileUri(value.id) ? value.id : "",
  );
};

const fileIdFromValue = (value) => {
  if (typeof value === "string") return isOpenableFileUri(value) ? "" : value;
  if (!value || typeof value !== "object") return "";
  return firstText(value.id, value.fileId, value.uuid, value.documentId, value.photoId);
};

const attachmentSnapshotString = (value) => {
  const data = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const fileId = fileIdFromValue(value);
  const fileUri = fileUriFromValue(value);
  const displayValue = Object.entries({
    fileId: fileId || undefined,
    url: fileUri || undefined,
    name: firstText(data.name, data.fileName, data.documentName, data.title, data.certificationName, data.caption) || undefined,
    type: firstText(data.type, data.documentType, data.mimeType, data.contentType) || undefined,
    issuer: firstText(data.issuer, data.issuingOrganization) || undefined,
    issueDate: firstText(data.issueDate, data.issuedAt) || undefined,
    expiryDate: firstText(data.expiryDate, data.expiresAt, data.expirationDate) || undefined,
  }).reduce((acc, [key, nextValue]) => {
    if (nextValue) acc[key] = nextValue;
    return acc;
  }, {});
  return Object.keys(displayValue).length ? JSON.stringify(displayValue) : "";
};

const snapshotAttachmentPayloadValue = (value) => {
  const parsed = tryParseSnapshotValue(value);
  const fileId = fileIdFromValue(parsed || value);
  if (!fileId) return "";
  return JSON.stringify({ fileId });
};

const prepareProfileSnapshotForSubmission = (snapshot) =>
  snapshot
    .filter((item) => !item.excluded)
    .map((item) => {
      if (!ATTACHMENT_SNAPSHOT_SECTIONS.includes(item.sectionKey)) return item;
      return {
        ...item,
        value: snapshotAttachmentPayloadValue(item.value),
      };
    })
    .filter((item) => String(item.value || "").trim())
    .map(({ excluded, ...item }) => item);

const attachmentDisplayParts = (sectionKey, value, index = 0) => {
  const data = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const fileUri = fileUriFromValue(value);
  const fileId = fileIdFromValue(value);
  const hasAttachment = Boolean(fileUri || fileId);
  if (sectionKey === "photos") {
    return {
      title: firstText(data.caption, data.name, data.fileName, data.title, `Photo ${index + 1}`),
      subtitle: fileUri ? "Photo preview available" : fileId ? "Photo attached" : "",
      meta: firstText(data.uploadedAt, data.createdAt, data.mimeType, data.contentType),
      fileUri,
      hasAttachment,
    };
  }
  if (sectionKey === "certifications") {
    return {
      title: firstText(data.name, data.title, data.certificationName, `Certification ${index + 1}`),
      subtitle: compactTextList([data.issuer, data.issuingOrganization, data.certificationNumber]).join(" • "),
      meta: compactTextList([data.issueDate, data.issuedAt, data.expiryDate, data.expiresAt, fileId && !fileUri ? "Certificate attached" : ""]).join(" • "),
      fileUri,
      hasAttachment,
    };
  }
  if (sectionKey === "licenses") {
    return {
      title: firstText(data.name, data.title, data.licenseName, `License ${index + 1}`),
      subtitle: compactTextList([data.issuer, data.issuingOrganization, data.certificationNumber, data.licenseNumber]).join(" • "),
      meta: compactTextList([data.issueDate, data.issuedAt, data.expiryDate, data.expiresAt, fileId && !fileUri ? "License attached" : ""]).join(" • "),
      fileUri,
      hasAttachment,
    };
  }
  return {
    title: firstText(data.caption, data.name, data.fileName, data.documentName, data.type, `Document ${index + 1}`),
    subtitle: firstText(data.description, data.documentType, data.mimeType, data.contentType, fileUri ? "Document preview available" : ""),
    meta: firstText(data.status, data.uploadedAt, data.createdAt, fileId && !fileUri ? "Document attached" : ""),
    fileUri,
    hasAttachment,
  };
};

const snapshotDisplayParts = (sectionKey, value) => {
  const parsed = tryParseSnapshotValue(value);
  const item = Array.isArray(parsed) ? parsed[0] : parsed;
  if (ATTACHMENT_SNAPSHOT_SECTIONS.includes(sectionKey)) return attachmentDisplayParts(sectionKey, item || value);
  if (!item || typeof item !== "object") return null;

  if (sectionKey === "sectors") {
    return {
      title: firstText(item.name, item.title, item.label, item.sector, item.sectorName, item.code, value),
      subtitle: firstText(item.description, item.summary),
      meta: firstText(item.category, item.type),
    };
  }
  if (sectionKey === "references") {
    return {
      title: firstText(item.name, item.fullName, item.referenceName, "Reference"),
      subtitle: compactTextList([item.relationship, item.company, item.email, item.phone]).join(" • "),
      meta: firstText(item.notes, item.description),
    };
  }
  if (sectionKey === "workExperience") {
    return {
      title: firstText(item.title, item.role, item.position, item.employer, item.company, "Work experience"),
      subtitle: firstText(item.description, item.summary, item.responsibilities),
      meta: compactTextList([item.startDate, item.endDate, item.location]).join(" • "),
    };
  }
  if (sectionKey === "education") {
    return {
      title: firstText(item.schoolName, item.school, item.institution, item.degree, "Education"),
      subtitle: compactTextList([item.degree, item.fieldOfStudy, item.educationLevel]).join(" • "),
      meta: compactTextList([item.startDate, item.endDate, item.graduationYear]).join(" • "),
    };
  }
  return null;
};

const buildProfileSnapshot = (profile, user) => {
  if (!profile) return [];
  const entries = [];
  const add = (sectionKey, sectionLabel, key, label, value) => {
    const text = snapshotString(value).trim();
    if (!text) return;
    entries.push({ sectionKey, sectionLabel, key, label, value: text });
  };
  const addAttachment = (sectionKey, sectionLabel, key, label, value) => {
    const text = attachmentSnapshotString(value).trim();
    if (!text) return;
    entries.push({ sectionKey, sectionLabel, key, label, value: text });
  };

  add("userDetails", SNAPSHOT_SECTIONS.userDetails, "userDetails.name", "Name", firstText(user?.name, [user?.firstName, user?.lastName].filter(Boolean).join(" "), user?.email));
  add("userDetails", SNAPSHOT_SECTIONS.userDetails, "userDetails.email", "Email", user?.email);
  add("profile", SNAPSHOT_SECTIONS.profile, "profile.profileName", "Professional Profile Name", profileTitle(profile));
  add("profile", SNAPSHOT_SECTIONS.profile, "profile.profileSummary", "Professional Profile Summary", profile?.profileSummary || profile?.description);
  add("profile", SNAPSHOT_SECTIONS.profile, "profile.educationLevel", "Education Level", profile?.educationLevel);
  add("profile", SNAPSHOT_SECTIONS.profile, "profile.experience", "Experience", profile?.experience);
  (profile?.sectors || []).forEach((value, index) => add("sectors", SNAPSHOT_SECTIONS.sectors, `sectors.${index}`, `Sector ${index + 1}`, value));
  (profile?.previousRegionsWorkedIn || []).forEach((value, index) => add("regions", SNAPSHOT_SECTIONS.regions, `previousRegionsWorkedIn.${index}`, `Previous Region ${index + 1}`, value));
  (profile?.references || []).forEach((value, index) => add("references", SNAPSHOT_SECTIONS.references, `references.${index}`, `Reference ${index + 1}`, value));
  (profile?.workExperienceHistory || []).forEach((value, index) => add("workExperience", SNAPSHOT_SECTIONS.workExperience, `workExperienceHistory.${index}`, `Work Experience ${index + 1}`, value));
  (profile?.education || []).forEach((value, index) => add("education", SNAPSHOT_SECTIONS.education, `education.${index}`, `Education ${index + 1}`, value));
  (profile?.documents || []).forEach((value, index) => addAttachment("documents", SNAPSHOT_SECTIONS.documents, `documents.${index}`, `Document ${index + 1}`, value));
  (profile?.photos || []).forEach((value, index) => addAttachment("photos", SNAPSHOT_SECTIONS.photos, `photos.${index}`, `Photo ${index + 1}`, value));
  (profile?.certifications || []).forEach((value, index) => addAttachment("certifications", SNAPSHOT_SECTIONS.certifications, `certifications.${index}`, `Certification ${index + 1}`, value));
  (profile?.licenses || []).forEach((value, index) => addAttachment("licenses", SNAPSHOT_SECTIONS.licenses, `licenses.${index}`, `License ${index + 1}`, value));
  profileServices(profile).forEach((value, index) => add("interestedServices", SNAPSHOT_SECTIONS.interestedServices, `interestedServices.${index}`, `Interested Service ${index + 1}`, value));
  profileLocations(profile).forEach((value, index) => add("interestedLocations", SNAPSHOT_SECTIONS.interestedLocations, `interestedLocations.${index}`, `Interested Location ${index + 1}`, value));
  (profile?.interestedShifts || []).forEach((value, index) => add("interestedShifts", SNAPSHOT_SECTIONS.interestedShifts, `interestedShifts.${index}`, `Interested Shift ${index + 1}`, value));

  return entries;
};

const SnapshotEntryEditor = ({ item, onChange, onToggleIncluded, onPreviewAttachment }) => {
  const display = STRUCTURED_SNAPSHOT_SECTIONS.includes(item.sectionKey)
    ? snapshotDisplayParts(item.sectionKey, item.value)
    : null;

  if (display) {
    return (
      <div className={`job-application-snapshot-item job-application-snapshot-item--structured${item.excluded ? " job-application-snapshot-item--excluded" : ""}`}>
        <div className="job-application-snapshot-item__header">
          <div>
            <Text className="job-application-snapshot-item__section">{item.sectionLabel}</Text>
            <strong>{item.label}</strong>
          </div>
          <Button
            icon={item.excluded ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={onToggleIncluded}
            className={item.excluded ? "job-application-snapshot-include" : "job-application-snapshot-exclude"}
          >
            {item.excluded ? "Include" : "Exclude"}
          </Button>
        </div>
        <div className="job-application-snapshot-card">
          <span className="job-application-snapshot-card__icon"><PaperClipOutlined /></span>
          <div>
            <strong>{display.title}</strong>
            {display.subtitle ? <span>{display.subtitle}</span> : null}
            {display.meta ? <Text type="secondary">{display.meta}</Text> : null}
          </div>
          {display.fileUri || display.hasAttachment ? (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onPreviewAttachment(display)}
              disabled={!display.fileUri}
            >
              Preview
            </Button>
          ) : null}
        </div>
        {item.excluded ? <Text className="job-application-snapshot-excluded-text">Excluded from submitted snapshot</Text> : null}
      </div>
    );
  }

  const multiline = item.value.length > 80 || item.value.includes("{") || item.value.includes("[");
  return (
    <div className={`job-application-snapshot-item${item.excluded ? " job-application-snapshot-item--excluded" : ""}`}>
      <div className="job-application-snapshot-item__header">
        <div>
          <Text className="job-application-snapshot-item__section">{item.sectionLabel}</Text>
          <strong>{item.label}</strong>
        </div>
        <Button
          icon={item.excluded ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          onClick={onToggleIncluded}
          className={item.excluded ? "job-application-snapshot-include" : "job-application-snapshot-exclude"}
        >
          {item.excluded ? "Include" : "Exclude"}
        </Button>
      </div>
      {multiline ? (
        <TextArea rows={3} value={item.value} disabled={item.excluded} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <Input value={item.value} disabled={item.excluded} onChange={(event) => onChange(event.target.value)} />
      )}
      {item.excluded ? <Text className="job-application-snapshot-excluded-text">Excluded from submitted snapshot</Text> : null}
    </div>
  );
};

const answerToText = (value) => (Array.isArray(value) ? value.filter(Boolean).join(", ") : String(value || "").trim());

const questionText = (question) =>
  firstText(question?.question, question?.questionText, question?.text, question?.title, question?.label);

const questionOptions = (question) => {
  const options = question?.answerOptions;
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => {
      if (typeof option === "string" || typeof option === "number" || typeof option === "boolean") {
        const value = String(option);
        return { label: value, value };
      }
      return {
        label: firstText(option?.label, option?.name, option?.title, option?.value, option?.text),
        value: firstText(option?.value, option?.label, option?.name, option?.title, option?.text),
      };
    })
    .filter((option) => option.label && option.value);
};

const isQuestionRequired = (question) => question?.isRequired !== false;

const sortQuestions = (questions = []) =>
  [...questions].sort((left, right) => {
    const leftOrder = Number(left.orderNumber ?? Number.MAX_SAFE_INTEGER);
    const rightOrder = Number(right.orderNumber ?? Number.MAX_SAFE_INTEGER);
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return String(left.createdAt || "").localeCompare(String(right.createdAt || ""));
  });

const buildQuestionAnswersNote = (questions, answers) => {
  const lines = questions
    .map((question, index) => {
      const id = question.id || `${index}`;
      const answer = answerToText(answers[id]);
      const title = questionText(question);
      if (!title || !answer) return "";
      const metric = question.answerMetric || question.metric;
      return `Q: ${title}${metric ? `\nMetric: ${metric}` : ""}\nA: ${answer}`;
    })
    .filter(Boolean);
  return lines.length ? `Application question answers:\n\n${lines.join("\n\n")}` : "";
};

const QuestionInput = ({ question, index, value, onChange }) => {
  const type = String(question.answerType || "TEXT").toUpperCase();
  const options = questionOptions(question);
  const required = isQuestionRequired(question);
  const title = questionText(question);

  return (
    <Card className="job-application-question">
      <Text className="job-application-question__label">
        {index + 1}. {title}{required ? " *" : ""}
      </Text>
      {question.answerMetric || question.metric ? (
        <Text type="secondary">{question.answerMetric || question.metric}</Text>
      ) : null}
      {options.length && type === "SINGLE_CHOICE" ? (
        <Radio.Group value={value} onChange={(event) => onChange(event.target.value)}>
          <Space direction="vertical">
            {options.map((option) => <Radio key={option.value} value={option.value}>{option.label}</Radio>)}
          </Space>
        </Radio.Group>
      ) : options.length && type === "MULTIPLE_CHOICE" ? (
        <Checkbox.Group options={options} value={Array.isArray(value) ? value : []} onChange={onChange} />
      ) : (
        <TextArea
          rows={type === "BOOLEAN" || type === "NUMBER" ? 1 : 3}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Your answer"
        />
      )}
    </Card>
  );
};

const applicationTitle = (application) =>
  firstText(
    application?.jobOpportunity?.title,
    application?.jobOpportunityTitle,
    application?.jobTitle,
    application?.title,
  ) || "Job Application";

const applicationJobId = (application) =>
  firstText(application?.jobOpportunityId, application?.jobPostId, application?.jobOpportunity?.id, application?.jobPost?.id);

const applicantName = (application) => {
  const applicant = application?.applicant || application?.user || application?.applicantProfile || {};
  return firstText(
    applicant.fullName,
    applicant.displayName,
    applicant.name,
    [applicant.firstName, applicant.lastName].filter(Boolean).join(" "),
    application?.applicantName,
    application?.userName,
  ) || "Applicant";
};

const employerName = (application) =>
  namedRefLabel(application?.jobOpportunity?.workspace) ||
  namedRefLabel(application?.jobPost?.workspace) ||
  namedRefLabel(application?.workspace) ||
  namedRefLabel(application?.jobOpportunity?.poster) ||
  namedRefLabel(application?.jobPost?.poster) ||
  "Employer";

const applicationStatusUpdates = (application) =>
  pageContent(
    application?.statusUpdates ||
      application?.statusHistory ||
      application?.history ||
      application?.jobApplicationStatusUpdates,
  );

const statusUpdateTime = (item) => {
  const value = firstText(item?.addedAt, item?.createdAt, item?.updatedAt, item?.date, item?.timestamp);
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

const snapshotValue = (application, ...keys) =>
  firstText(application?.professionProfileSnapshot?.find((entry) => keys.includes(entry.key))?.value);

const applicationProfileLabel = (application) =>
  firstText(
    application?.professionProfile?.profileName,
    application?.professionProfile?.title,
    application?.professionProfileName,
    snapshotValue(application, "profileName", "profile.profileName"),
    application?.professionProfileId,
  );

const parseQuestionAnswers = (note) => {
  const text = firstText(note);
  const marker = "Application question answers:";
  const markerIndex = text.indexOf(marker);
  if (markerIndex < 0) return [];
  return text
    .slice(markerIndex + marker.length)
    .trim()
    .split(/\n{2,}/)
    .map((block) => {
      const question = firstText(block.match(/^Q:\s*(.+)$/m)?.[1]);
      const metric = firstText(block.match(/^Metric:\s*(.+)$/m)?.[1]);
      const answer = firstText(block.match(/^A:\s*([\s\S]+)$/m)?.[1]);
      return question && answer ? { question, metric, answer } : null;
    })
    .filter(Boolean);
};

const statusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "SUBMITTED" || normalized === "APPLIED" || normalized === "NEW") return "submitted";
  if (normalized === "HIRED" || normalized === "HIRED_CLOSED") return "hired";
  if (normalized.includes("REJECT") || normalized.includes("CANCEL") || normalized.includes("WITHDRAW")) return "rejected";
  return "active";
};

const ApplicationCard = ({ application, onOpen }) => (
  <button type="button" className="job-application-card-row" onClick={onOpen}>
    <div className="job-application-card-row__top">
      <span className="job-application-card-row__icon"><FileTextOutlined /></span>
      <div className="job-application-card-row__copy">
        <span className="job-application-card-row__title">{applicationTitle(application)}</span>
        <span className="job-application-card-row__meta">
          <ClockCircleOutlined /> Submitted {formatDate(application.createdAt || application.submittedAt) || "Recently"}
        </span>
        <Space wrap>
          <Tag className={`job-application-card-row__status is-${statusTone(application.status)}`}>
            {titleCase(application.status || "Submitted")}
          </Tag>
          {application.shareProfile ? <Tag color="green">Profile shared</Tag> : null}
          {application.shareBackgroundCheck ? <Tag color="blue">Background check shared</Tag> : null}
        </Space>
      </div>
      <Button
        className="job-application-card-row__details"
        icon={<EyeOutlined />}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        Details
      </Button>
    </div>
  </button>
);

const ApplicationPreviewDrawer = ({
  application,
  open,
  loading,
  error,
  isHiringContext,
  onClose,
  onOpenDetails,
  onOpenJobPost,
}) => {
  const jobId = applicationJobId(application);
  const noteText = firstText(application?.note);
  const history = useMemo(
    () => [...applicationStatusUpdates(application)].sort((left, right) => statusUpdateTime(right) - statusUpdateTime(left)),
    [application],
  );
  const questionAnswers = useMemo(() => parseQuestionAnswers(application?.note), [application?.note]);
  const profileLabel = applicationProfileLabel(application);
  const backgroundCheckLabelText = firstText(
    application?.backgroundCheck?.checkType,
    application?.backgroundCheck?.status,
    application?.backgroundCheckStatus,
    application?.backgroundCheckId,
  );
  const applicantEmail = firstText(
    application?.applicant?.email,
    application?.applicant?.primaryEmail,
    application?.user?.email,
    application?.user?.primaryEmail,
  );
  const applicantPhone = firstText(
    application?.applicant?.phone,
    application?.applicant?.phoneNumber,
    application?.user?.phone,
    application?.user?.phoneNumber,
  );
  const workspaceLabel = employerName(application);

  return (
    <Drawer
      title={isHiringContext ? "Applicant details" : "Application details"}
      open={open}
      onClose={onClose}
      width="min(520px, 100vw)"
      className="job-applications-preview-drawer"
      destroyOnClose
    >
      {loading && !application ? (
        <Skeleton active />
      ) : !application ? (
        <Empty description="Select an application to preview." />
      ) : (
        <Space direction="vertical" size={14} className="job-applications-preview-drawer__stack">
          {loading ? <Alert type="info" showIcon message="Refreshing application details..." /> : null}
          {error ? <Alert type="warning" showIcon message="Unable to load latest details" description={error} /> : null}

          <Card className="job-applications-preview-drawer__hero">
            <div className="job-applications-preview-drawer__hero-top">
              <span className="job-applications-preview-drawer__icon"><FileTextOutlined /></span>
              <div>
                <Tag className={`job-application-card-row__status is-${statusTone(application.status)}`}>
                  {titleCase(application.status || "Submitted")}
                </Tag>
                <Title level={4}>{applicationTitle(application)}</Title>
                <Text type="secondary">
                  {isHiringContext ? applicantName(application) : employerName(application)}
                </Text>
              </div>
            </div>
            <div className="job-applications-preview-drawer__actions">
              <Button type="primary" icon={<EyeOutlined />} onClick={onOpenDetails}>
                View full details
              </Button>
              <Button icon={<FileTextOutlined />} disabled={!jobId} onClick={onOpenJobPost}>
                Job post
              </Button>
            </div>
          </Card>

          <div className="job-applications-preview-drawer__details">
            <div>
              <Text>Status</Text>
              <strong>{titleCase(application.status || "Submitted")}</strong>
            </div>
            <div>
              <Text>Submitted</Text>
              <strong>{formatDate(application.createdAt || application.submittedAt) || "Not set"}</strong>
            </div>
            <div>
              <Text>Updated</Text>
              <strong>{formatDate(application.updatedAt) || "Not set"}</strong>
            </div>
            <div>
              <Text>{isHiringContext ? "Applicant" : "Employer"}</Text>
              <strong>{isHiringContext ? applicantName(application) : employerName(application)}</strong>
            </div>
            <div>
              <Text>Workspace</Text>
              <strong>{workspaceLabel}</strong>
            </div>
            <div>
              <Text>Profile</Text>
              <strong>{profileLabel || (application.shareProfile ? "Shared" : "Not shared")}</strong>
            </div>
            <div>
              <Text>Background check</Text>
              <strong>{backgroundCheckLabelText || (application.shareBackgroundCheck ? "Shared" : "Not shared")}</strong>
            </div>
            <div>
              <Text>Job ID</Text>
              <strong>{jobId || "Not set"}</strong>
            </div>
            <div>
              <Text>Application ID</Text>
              <strong>{application.id || "Not set"}</strong>
            </div>
          </div>

          {isHiringContext && (applicantEmail || applicantPhone || profileLabel) ? (
            <Card className="job-applications-preview-drawer__section">
              <Text type="secondary">Applicant snapshot</Text>
              <div className="job-applications-preview-drawer__mini-list">
                {profileLabel ? <span><UserOutlined /> Profile: <strong>{profileLabel}</strong></span> : null}
                {applicantEmail ? <span><UserOutlined /> Email: <strong>{applicantEmail}</strong></span> : null}
                {applicantPhone ? <span><UserOutlined /> Phone: <strong>{applicantPhone}</strong></span> : null}
                <span><SafetyCertificateOutlined /> Background: <strong>{application.shareBackgroundCheck ? "Shared" : "Not shared"}</strong></span>
              </div>
            </Card>
          ) : null}

          {history.length ? (
            <Card className="job-applications-preview-drawer__section">
              <Text type="secondary">Latest status history</Text>
              <div className="job-applications-preview-drawer__history">
                {history.slice(0, 3).map((item, index) => (
                  <div key={`${item.status || "status"}-${item.addedAt || item.createdAt || index}`}>
                    <Tag>{titleCase(item.status || "Updated")}</Tag>
                    <Text>{formatDate(item.addedAt || item.createdAt || item.updatedAt) || "Recently"}</Text>
                    {firstText(item.noteToApplicant, item.noteToHiringTeam, item.note, item.internalNote) ? (
                      <Paragraph ellipsis={{ rows: 2 }}>
                        {firstText(item.noteToApplicant, item.noteToHiringTeam, item.note, item.internalNote)}
                      </Paragraph>
                    ) : null}
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {questionAnswers.length ? (
            <Card className="job-applications-preview-drawer__section">
              <Text type="secondary">Application questions</Text>
              <div className="job-applications-preview-drawer__questions">
                {questionAnswers.slice(0, 3).map((item, index) => (
                  <div key={`${item.question}-${index}`}>
                    <strong>Q: {item.question}</strong>
                    {item.metric ? <Text>Metric: {item.metric}</Text> : null}
                    <Paragraph ellipsis={{ rows: 2 }}>A: {item.answer}</Paragraph>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {noteText ? (
            <Card className="job-applications-preview-drawer__note">
              <Text type="secondary">{isHiringContext ? "Candidate message" : "Application note"}</Text>
              <Paragraph ellipsis={{ rows: 5 }}>{noteText}</Paragraph>
            </Card>
          ) : null}
        </Space>
      )}
    </Drawer>
  );
};

export default function JobApplications() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, isEmployerWorkspace, isProfessionalWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const jobOpportunityId = useMemo(() => queryParam(location.search, "jobOpportunityId"), [location.search]);
  const requestedContext = useMemo(() => String(queryParam(location.search, "context") || "").toLowerCase(), [location.search]);
  const jobPostReviewMode = Boolean(jobOpportunityId && ["hiring", "employer", "review"].includes(requestedContext));
  const applicationListMode = !jobOpportunityId || jobPostReviewMode;

  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationsError, setApplicationsError] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [job, setJob] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profileSnapshot, setProfileSnapshot] = useState([]);
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [selectedBackgroundCheckId, setSelectedBackgroundCheckId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loadingApply, setLoadingApply] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [step, setStep] = useState(0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [previewApplication, setPreviewApplication] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const selectedProfile = useMemo(
    () => profiles.find((profile) => profile.id === selectedProfileId),
    [profiles, selectedProfileId],
  );

  const selectedBackgroundCheck = useMemo(
    () => backgroundChecks.find((check) => check.id === selectedBackgroundCheckId),
    [backgroundChecks, selectedBackgroundCheckId],
  );

  const profileSnapshotSections = useMemo(() => {
    const groups = profileSnapshot.reduce((acc, item) => {
      const key = item.sectionKey || "profile";
      if (!acc.has(key)) {
        acc.set(key, {
          key,
          label: item.sectionLabel || SNAPSHOT_SECTIONS[key] || titleCase(key),
          items: [],
        });
      }
      acc.get(key).items.push(item);
      return acc;
    }, new Map());
    return Array.from(groups.values());
  }, [profileSnapshot]);

  const filteredApplications = useMemo(() => {
    if (statusFilter === "ALL") return applications;
    return applications.filter((application) => String(application.status || "").toUpperCase() === statusFilter);
  }, [applications, statusFilter]);

  const promotionItems = useMemo(
    () => [
      {
        eyebrow: "Sponsored",
        slotLabel: "Ad space",
        placeholder: true,
        icon: "rocket",
        pinned: true,
      },
      {
        eyebrow: "Profile strength",
        title: "Keep your profile current",
        titleLevel: 5,
        description: "Refresh skills, documents, and availability before the next review.",
        actionLabel: "Improve profile",
        featured: true,
        icon: "profile",
        onAction: () => navigate(paths.professionalProfiles),
      },
      {
        eyebrow: "Background checks",
        title: "Be ready to start faster",
        titleLevel: 5,
        description: "Eligible checks can reduce delays once a team moves forward.",
        actionLabel: "Request background check",
        actionLabelLines: ["Request", "background check"],
        icon: "backgroundCheck",
        onAction: () => navigate(paths.backgroundChecks),
      },
      {
        eyebrow: "Application progress",
        title: "Track every next step",
        titleLevel: 5,
        description: "Review status updates, interviews, and hiring notes in one place.",
        icon: "hiring",
      },
    ],
    [navigate],
  );

  const loadApplications = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId || !applicationListMode) return;
    setApplicationsLoading(true);
    setApplicationsError("");
    try {
      const commonParams = {
        actorId,
        workspaceId: selectedWorkspaceId,
        status: statusFilter === "ALL" ? undefined : statusFilter,
        page: 0,
        size: 100,
      };
      const result = jobPostReviewMode
        ? await olreadiaService.jobApplications.listForJobPost({
          ...commonParams,
          jobOpportunityId,
        })
        : isEmployerWorkspace
          ? await olreadiaService.jobApplications.listPostedJobs(commonParams)
          : await olreadiaService.jobApplications.listMine({
            ...commonParams,
            applicantId: actorId,
          });
      setApplications(result.content || []);
    } catch (error) {
      setApplications([]);
      setApplicationsError(error?.message || "Unable to load applications.");
    } finally {
      setApplicationsLoading(false);
    }
  }, [actorId, applicationListMode, isEmployerWorkspace, jobOpportunityId, jobPostReviewMode, selectedWorkspaceId, statusFilter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    let cancelled = false;
    const loadApplyFlow = async () => {
      if (!jobOpportunityId || jobPostReviewMode || !actorId) return;
      setLoadingApply(true);
      setApplyError("");
      try {
        const [jobResult, profileResult, questionResult] = await Promise.all([
          olreadiaService.jobOpportunities.get(jobOpportunityId, actorId),
          olreadiaService.professionProfiles.listForUser(actorId, {
            workspaceId: selectedWorkspaceId,
            page: 0,
            size: 100,
          }),
          olreadiaService.jobOpportunityQuestions.list(jobOpportunityId, actorId),
        ]);
        if (cancelled) return;
        const profileList = pageContent(profileResult);
        setJob(jobResult);
        setProfiles(profileList);
        setQuestions(sortQuestions(pageContent(questionResult)).filter(questionText));
        if (profileList.length && !selectedProfileId) setSelectedProfileId(profileList[0].id || "");
      } catch (error) {
        if (!cancelled) setApplyError(error?.message || "Unable to load job application flow.");
      } finally {
        if (!cancelled) setLoadingApply(false);
      }
    };
    loadApplyFlow();
    return () => {
      cancelled = true;
    };
  }, [actorId, jobOpportunityId, jobPostReviewMode, selectedProfileId, selectedWorkspaceId]);

  useEffect(() => {
    setProfileSnapshot(buildProfileSnapshot(selectedProfile, user));
  }, [selectedProfile, user]);

  useEffect(() => {
    let cancelled = false;
    const loadBackgroundChecks = async () => {
      if (!selectedProfileId) {
        setBackgroundChecks([]);
        setSelectedBackgroundCheckId("");
        return;
      }
      try {
        const result = await olreadiaService.backgroundChecks.listForProfessionProfile(selectedProfileId);
        if (cancelled) return;
        const checks = pageContent(result).filter(eligibleBackgroundCheck);
        setBackgroundChecks(checks);
        if (checks.length) setSelectedBackgroundCheckId((current) => current || checks[0].id || "");
      } catch {
        if (!cancelled) {
          setBackgroundChecks([]);
          setSelectedBackgroundCheckId("");
        }
      }
    };
    loadBackgroundChecks();
    return () => {
      cancelled = true;
    };
  }, [selectedProfileId]);

  const updateProfileSnapshotEntry = (key, value) => {
    setProfileSnapshot((current) => current.map((item) => (item.key === key ? { ...item, value } : item)));
  };

  const toggleProfileSnapshotEntry = (key) => {
    setProfileSnapshot((current) =>
      current.map((item) => (item.key === key ? { ...item, excluded: !item.excluded } : item)),
    );
  };

  const toggleProfileSnapshotSection = (sectionKey, excluded) => {
    setProfileSnapshot((current) =>
      current.map((item) => (item.sectionKey === sectionKey ? { ...item, excluded } : item)),
    );
  };

  const validateCurrentStep = () => {
    if (step === 0 && !selectedProfileId) {
      message.warning("Select a professional profile to continue.");
      return false;
    }
    if (step === 3) {
      const missingQuestion = questions.find((question, index) => {
        if (!isQuestionRequired(question)) return false;
        const id = question.id || `${index}`;
        return !answerToText(answers[id]);
      });
      if (missingQuestion) {
        message.warning("Please answer all required application questions.");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateCurrentStep()) return;
    setStep((current) => Math.min(current + 1, stepItems.length - 1));
  };

  const submitApplication = async () => {
    if (!job?.id || !actorId || !selectedWorkspaceId || !selectedProfileId) {
      message.warning("Application details are incomplete.");
      return;
    }
    if (!validateCurrentStep()) return;

    const questionAnswersNote = buildQuestionAnswersNote(questions, answers);
    const noteParts = [note.trim(), questionAnswersNote].filter(Boolean);
    setSubmitting(true);
    try {
      await olreadiaService.jobApplications.create({
        jobOpportunityId: job.id,
        applicantId: actorId,
        workspaceId: selectedWorkspaceId,
        professionProfileId: selectedProfileId,
        backgroundCheckId: selectedBackgroundCheckId || null,
        shareProfile: true,
        shareBackgroundCheck: Boolean(selectedBackgroundCheckId),
        professionProfileSnapshot: prepareProfileSnapshotForSubmission(profileSnapshot),
        note: noteParts.join("\n\n") || null,
      }, actorId);
      message.success("Application submitted.");
      navigate(paths.jobApplications);
    } catch (error) {
      message.error(error?.message || "Unable to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepItems = [
    { title: "Profile" },
    { title: "Preview" },
    { title: "Background" },
    { title: "Questions" },
    { title: "Submit" },
  ];

  const openApplicationPreview = async (application) => {
    setPreviewApplication(application);
    setPreviewError("");
    if (!application?.id || !actorId) return;
    setPreviewLoading(true);
    try {
      const result = await olreadiaService.jobApplications.get(application.id, actorId);
      setPreviewApplication(result || application);
    } catch (error) {
      setPreviewError(error?.message || "Showing the row details we already have.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeApplicationPreview = () => {
    setPreviewApplication(null);
    setPreviewError("");
    setPreviewLoading(false);
  };

  const openPreviewDetailsPage = () => {
    if (!previewApplication?.id) return;
    navigate(paths.jobApplicationDetails(previewApplication.id));
  };

  const openPreviewJobPost = () => {
    const id = applicationJobId(previewApplication);
    if (!id) return;
    navigate(paths.jobOpportunityDetails(id));
  };

  if (applicationListMode) {
    const listTitle = jobPostReviewMode
      ? "Job Post Applications"
      : isEmployerWorkspace
        ? "Posted Job Applications"
        : "My Applications";
    const listDescription = jobPostReviewMode
      ? "Review candidates who applied to this job post."
      : isEmployerWorkspace
        ? "Review applications submitted to jobs in this workspace."
        : "Review submitted applications, statuses, and next steps.";

    return (
      <div className="job-applications-page">
        <div className="job-applications-list-layout">
          <main className="job-applications-list-main">
            <section className="job-applications-header job-applications-header--list">
              <div>
                <Text className="job-applications-eyebrow">{isEmployerWorkspace || jobPostReviewMode ? "Hiring" : "My Job Search"}</Text>
                <Title level={2}>{listTitle}</Title>
                <Paragraph>{listDescription}</Paragraph>
              </div>
              {isEmployerWorkspace || jobPostReviewMode ? (
                <Button icon={<FileTextOutlined />} type="primary" onClick={() => navigate(paths.jobPosts)}>
                  Job posts
                </Button>
              ) : (
                <Button icon={<SendOutlined />} type="primary" onClick={() => navigate(paths.jobSearch)}>
                  Find jobs
                </Button>
              )}
            </section>

            <Card className="job-applications-list-card">
              <Space wrap className="job-applications-filter-row">
                {APPLICATION_STATUS_FILTERS.map((status) => (
                  <Button
                    key={status}
                    className={`job-applications-filter-chip${statusFilter === status ? " job-applications-filter-chip--selected" : ""}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {titleCase(status)}
                  </Button>
                ))}
              </Space>

              {applicationsError ? (
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load applications"
                  description={applicationsError}
                  action={<Button onClick={loadApplications}>Retry</Button>}
                />
              ) : null}

              <List
                className="job-applications-list"
                loading={applicationsLoading}
                dataSource={filteredApplications}
                renderItem={(application) => (
                  <List.Item className="job-applications-list-item">
                    <ApplicationCard
                      application={application}
                      onOpen={() => openApplicationPreview(application)}
                    />
                  </List.Item>
                )}
                locale={{
                  emptyText: applicationsLoading ? (
                    <Skeleton active />
                  ) : (
                    <Empty description="No applications found" />
                  ),
                }}
              />
            </Card>
          </main>

          <SidePromotionSection items={promotionItems} />
        </div>
        <ApplicationPreviewDrawer
          application={previewApplication}
          open={Boolean(previewApplication)}
          loading={previewLoading}
          error={previewError}
          isHiringContext={isEmployerWorkspace || jobPostReviewMode}
          onClose={closeApplicationPreview}
          onOpenDetails={openPreviewDetailsPage}
          onOpenJobPost={openPreviewJobPost}
        />
      </div>
    );
  }

  return (
    <div className="job-applications-page">
      <div className="job-application-layout">
        <main className="job-application-main">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobSearch)} className="job-applications-back">
            Back to jobs
          </Button>

          <section className="job-application-hero">
            <span className="job-application-hero__icon"><SendOutlined /></span>
            <div>
              <Text className="job-applications-eyebrow">Apply to job</Text>
              <Title level={2}>{job?.title || "Job Application"}</Title>
              <Paragraph>{job ? namedRefLabel(job.workspace) || namedRefLabel(job.poster) || "Employer" : "Loading job details..."}</Paragraph>
            </div>
          </section>

          {applyError ? (
            <Alert type="error" showIcon message="Unable to start application" description={applyError} className="job-application-alert" />
          ) : null}

          {loadingApply ? (
            <Skeleton active />
          ) : (
            <div className="job-application-form-grid">
              <Card className="job-application-stepper-card">
                <Steps direction="vertical" current={step} items={stepItems} />
              </Card>

              <Card className="job-application-panel">
            {step === 0 ? (
              <>
                <Title level={4}>Select Professional Profile</Title>
                {profiles.length ? (
                  <Radio.Group value={selectedProfileId} onChange={(event) => {
                    setSelectedProfileId(event.target.value);
                    setStep(0);
                  }} className="job-application-radio-list">
                    {profiles.map((profile) => (
                      <Radio.Button key={profile.id || profileTitle(profile)} value={profile.id} className="job-application-select-card">
                        <ProfileSelectOption profile={profile} />
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                ) : (
                  <div className="job-application-create-profile">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        isProfessionalWorkspace
                          ? "Create a professional profile before applying."
                          : "No professional profiles found"
                      }
                    />
                    {isProfessionalWorkspace ? (
                      <>
                        <Paragraph>
                          Olreadia applications use your reusable profile so employers can review your skills,
                          experience, documents, services, and locations without repeated setup.
                        </Paragraph>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => navigate(paths.professionalProfileCreate)}
                        >
                          Create profile
                        </Button>
                      </>
                    ) : null}
                  </div>
                )}
              </>
            ) : null}

            {step === 1 ? (
              <>
                <Title level={4}>Profile Preview</Title>
                {selectedProfile ? (
                  <div className="job-application-preview">
                    <div className="job-application-preview-header">
                      <span className="job-application-preview-icon"><UserOutlined /></span>
                      <div>
                        <Title level={5}>{profileTitle(selectedProfile)}</Title>
                        <Paragraph>{profileSubtitle(selectedProfile)}</Paragraph>
                      </div>
                    </div>
                    <Button icon={<EditOutlined />} onClick={() => navigate(paths.professionalProfiles)} className="job-application-edit-profile">
                      Edit profile
                    </Button>
                    <Space wrap className="job-application-preview-chips">
                      {profileServices(selectedProfile).map((value) => <Tag key={value}>{value}</Tag>)}
                      {profileLocations(selectedProfile).map((value) => <Tag key={value}>{value}</Tag>)}
                      {profileShifts(selectedProfile).map((value) => <Tag key={value}>{value}</Tag>)}
                    </Space>
                    <div className="job-application-facts">
                      <div>
                        <Text type="secondary">Education</Text>
                        <strong>{titleCase(selectedProfile.educationLevel) || "Not set"}</strong>
                      </div>
                      <div>
                        <Text type="secondary">Experience</Text>
                        <strong>{firstText(selectedProfile.experience, selectedProfile.yearsOfExperience) || "Not set"}</strong>
                      </div>
                      <div>
                        <Text type="secondary">Files</Text>
                        <strong>
                          {(selectedProfile.documents?.length || 0) +
                            (selectedProfile.photos?.length || 0) +
                            (selectedProfile.certifications?.length || 0) +
                            (selectedProfile.licenses?.length || 0)}
                        </strong>
                      </div>
                    </div>

                    {profileSnapshotSections.length ? (
                      <div className="job-application-snapshot">
                        <div className="job-application-snapshot__header">
                          <div>
                            <Text className="job-application-preview-section__title">Application Snapshot</Text>
                            <Paragraph className="job-application-snapshot__help">
                              These edits change only the profile snapshot submitted with this application.
                            </Paragraph>
                          </div>
                          <Tag color="green">{profileSnapshot.filter((item) => !item.excluded).length} included</Tag>
                        </div>
                        <div className="job-application-snapshot__list">
                          {profileSnapshotSections.map((section) => {
                            const sectionExcluded = section.items.every((item) => item.excluded);
                            return (
                              <div key={section.key} className={`job-application-snapshot-section${sectionExcluded ? " job-application-snapshot-section--excluded" : ""}`}>
                                <div className="job-application-snapshot-section__header">
                                  <Text className="job-application-snapshot-section__title">{section.label}</Text>
                                  <Button
                                    size="small"
                                    icon={sectionExcluded ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                    onClick={() => toggleProfileSnapshotSection(section.key, !sectionExcluded)}
                                  >
                                    {sectionExcluded ? "Include section" : "Exclude section"}
                                  </Button>
                                </div>
                                <div className="job-application-snapshot-section__items">
                                  {section.items.map((item) => (
                                    <SnapshotEntryEditor
                                      key={item.key}
                                      item={item}
                                      onChange={(value) => updateProfileSnapshotEntry(item.key, value)}
                                      onToggleIncluded={() => toggleProfileSnapshotEntry(item.key)}
                                      onPreviewAttachment={(preview) => {
                                        if (!preview.fileUri) {
                                          message.warning("This attachment does not include a viewable file link.");
                                          return;
                                        }
                                        setAttachmentPreview(preview);
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : <Empty description="Select a profile first" />}
              </>
            ) : null}

            {step === 2 ? (
              <>
                <Title level={4}>Select Background Check</Title>
                <Paragraph type="secondary">Optional, but sharing a completed check can reduce waiting time before starting.</Paragraph>
                {backgroundChecks.length ? (
                  <Radio.Group value={selectedBackgroundCheckId} onChange={(event) => setSelectedBackgroundCheckId(event.target.value)} className="job-application-radio-list">
                    <Radio.Button value="" className="job-application-select-card">
                      <strong>Continue without a background check</strong>
                      <span>You can still submit your application.</span>
                    </Radio.Button>
                    {backgroundChecks.map((check) => (
                      <Radio.Button key={check.id || backgroundCheckLabel(check)} value={check.id} className="job-application-select-card">
                        <strong>{backgroundCheckLabel(check)}</strong>
                        <span>{formatDate(check.completedAt || check.requestedAt) || "Date unavailable"}</span>
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                ) : (
                  <Empty description="No completed background checks found. You can continue without one." />
                )}
              </>
            ) : null}

            {step === 3 ? (
              <>
                <Title level={4}>Application Questions</Title>
                {questions.length ? (
                  <Space direction="vertical" size={12} className="job-application-question-list">
                    {questions.map((question, index) => {
                      const id = question.id || `${index}`;
                      return (
                        <QuestionInput
                          key={id}
                          question={question}
                          index={index}
                          value={answers[id]}
                          onChange={(value) => setAnswers((current) => ({ ...current, [id]: value }))}
                        />
                      );
                    })}
                  </Space>
                ) : (
                  <Empty description="No application questions for this job." />
                )}
              </>
            ) : null}

            {step === 4 ? (
              <>
                <Title level={4}>Submit Application</Title>
                <div className="job-application-summary-row">
                  <UserOutlined />
                  <span>{selectedProfile ? profileTitle(selectedProfile) : "No profile selected"}</span>
                </div>
                <div className="job-application-summary-row">
                  <SafetyCertificateOutlined />
                  <span>{selectedBackgroundCheck ? backgroundCheckLabel(selectedBackgroundCheck) : "No background check selected"}</span>
                </div>
                <TextArea
                  rows={5}
                  value={note}
                  maxLength={1200}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Add a short note to the hiring team"
                  showCount
                />
              </>
            ) : null}

            <div className="job-application-actions">
              <Button onClick={() => setStep((current) => Math.max(current - 1, 0))} disabled={step === 0}>
                Back
              </Button>
              {step < stepItems.length - 1 ? (
                <Button type="primary" onClick={next}>
                  Continue
                </Button>
              ) : (
                <Button type="primary" icon={<CheckCircleOutlined />} loading={submitting} onClick={submitApplication}>
                  Submit Application
                </Button>
              )}
            </div>
              </Card>
            </div>
          )}
        </main>

        <SidePromotionSection className="job-application-promotions" items={promotionItems} />
      </div>
      <Modal
        open={Boolean(attachmentPreview)}
        title={attachmentPreview?.title || "Attachment Preview"}
        footer={attachmentPreview?.fileUri ? (
          <Button type="primary" href={attachmentPreview.fileUri} target="_blank" rel="noreferrer">
            Open in new tab
          </Button>
        ) : null}
        onCancel={() => setAttachmentPreview(null)}
        width="min(920px, 100vw)"
        destroyOnClose
      >
        {attachmentPreview?.fileUri ? (
          <iframe
            className="job-application-attachment-preview"
            title={attachmentPreview.title || "Attachment Preview"}
            src={attachmentPreview.fileUri}
          />
        ) : (
          <Empty description="Preview unavailable" />
        )}
      </Modal>
    </div>
  );
}
